import { useHealthCheck, useScheduleStats, useRewardPools, useNetworkStats } from '../hooks/use-api';
import { Spinner } from '../components/shared/Spinner';
import { StatusDot } from '../components/shared/StatusDot';
import type { StatusDotVariant } from '../components/shared/StatusDot';
import styles from './StatusPage.module.css';

type Subsystem = {
  readonly name: string;
  readonly label: string;
  readonly status: StatusDotVariant;
  readonly detail: string;
};

const StatusPage = (): React.ReactElement => {
  const health = useHealthCheck();
  const scheduleStats = useScheduleStats();
  const pools = useRewardPools();
  const stats = useNetworkStats();

  const isLoading = health.isLoading || scheduleStats.isLoading || pools.isLoading || stats.isLoading;

  const subsystems: ReadonlyArray<Subsystem> = (() => {
    const platformStatus = health.data?.status === 'ok' || health.data?.status === 'OK' ? 'ok' as const : health.isError ? 'err' as const : 'warn' as const;
    const platformDetail = health.data?.status === 'ok' || health.data?.status === 'OK' ? 'operational' : health.isError ? 'unreachable' : 'unknown';

    const protocolDetail = health.data ? 'deployed' : health.isError ? 'unreachable' : 'unknown';
    const protocolStatus: StatusDotVariant = health.data ? 'ok' : health.isError ? 'err' : 'warn';

    const storageStatus: StatusDotVariant = stats.data ? 'ok' : stats.isError ? 'err' : 'warn';
    const storageDetail = stats.data ? `${stats.data.totalEntities} entities` : stats.isError ? 'unreachable' : 'unknown';

    const vectorStatus: StatusDotVariant = stats.data ? 'ok' : 'warn';
    const vectorDetail = stats.data ? `${stats.data.totalBeliefs ?? 0} beliefs indexed` : 'unknown';

    const autonomyStatus: StatusDotVariant = scheduleStats.data ? 'ok' : scheduleStats.isError ? 'err' : 'warn';
    const autonomyDetail = scheduleStats.data ? `${scheduleStats.data.active} active schedules` : scheduleStats.isError ? 'unreachable' : 'unknown';

    const economyStatus: StatusDotVariant = pools.data ? 'ok' : pools.isError ? 'err' : 'warn';
    const poolBalance = pools.data?.pools.reduce((sum, p) => sum + p.balance, 0) ?? 0;
    const economyDetail = pools.data ? `${poolBalance.toLocaleString()} MC in pools` : pools.isError ? 'unreachable' : 'unknown';

    return [
      { name: 'platform', label: 'Platform', status: platformStatus, detail: platformDetail },
      { name: 'protocol', label: 'Agent Protocol', status: protocolStatus, detail: protocolDetail },
      { name: 'storage', label: 'Storage', status: storageStatus, detail: storageDetail },
      { name: 'vector', label: 'Vector Index', status: vectorStatus, detail: vectorDetail },
      { name: 'autonomy', label: 'Autonomy', status: autonomyStatus, detail: autonomyDetail },
      { name: 'economy', label: 'Economy', status: economyStatus, detail: economyDetail },
    ];
  })();

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>System Status</h1>

      {isLoading && (
        <div className={styles.loadingState}>
          <Spinner />
        </div>
      )}

      {!isLoading && (
        <div className={styles.statusGrid}>
          {subsystems.map((sub) => (
            <div key={sub.name} data-testid={`status-${sub.name}`} className={styles.statusCard}>
              <div className={styles.statusCardLabel}>{sub.label}</div>
              <div className={styles.statusCardRow}>
                <StatusDot variant={sub.status} />
                <span className={styles.statusCardDetail}>{sub.detail}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export { StatusPage };
